import { ethers } from 'ethers';
import { getContractAddress } from '../config/contracts';

export interface LotteryInfo {
  lotteryId: number;
  entryFee: string;
  maxParticipants: number;
  startTime: number;
  endTime: number;
  prizePool: string;
  participantCount: number;
  winner: string;
  isActive: boolean;
  isEnded: boolean;
  sequenceNumber: number;
}

export interface UserStats {
  winnings: string;
  participations: number;
}

export interface ContractStats {
  totalLotteries: number;
  totalPrizesDistributed: string;
  currentLotteryId: number;
}

class LotteryService {
  private contract: ethers.Contract | null = null;
  // Use concrete provider types so we can call getSigner safely
  private provider: ethers.providers.Web3Provider | ethers.providers.JsonRpcProvider | null = null;
  private signer: ethers.Signer | null = null;

  constructor() {
    this.initializeProvider();
  }

  private async initializeProvider() {
    try {
      const contractAddress = getContractAddress('SwapLottery');
      console.log('Initializing lottery service with contract address:', contractAddress);

      // Since we're using Hedera wallet interface, we'll just store the address
      // The actual contract calls will be handled by the ContractService
      console.log('Lottery service initialized with address:', contractAddress);

      // Contract service is now simplified for Hedera integration
    } catch (error) {
      console.error('Failed to initialize lottery service:', error);
      console.error('Error details:', error instanceof Error ? error.message : String(error));
      this.contract = null;
      this.provider = null;
      this.signer = null;
    }
  }

  // Get contract statistics
  async getContractStats(): Promise<ContractStats> {
    if (!this.contract) throw new Error('Contract not initialized');
    
    const [totalLotteries, totalPrizesDistributed, currentLotteryId] = await this.contract.getContractStats();
    
    return {
      totalLotteries: Number(totalLotteries),
      totalPrizesDistributed: ethers.utils.formatEther(totalPrizesDistributed),
      currentLotteryId: Number(currentLotteryId)
    };
  }

  // Get lottery information
  async getLottery(lotteryId: number): Promise<LotteryInfo> {
    if (!this.contract) throw new Error('Contract not initialized');
    
    const lottery = await this.contract.getLottery(lotteryId);
    
    return {
      lotteryId: Number(lottery[0]),
      entryFee: ethers.utils.formatEther(lottery[1]),
      maxParticipants: Number(lottery[2]),
      startTime: Number(lottery[3]),
      endTime: Number(lottery[4]),
      prizePool: ethers.utils.formatEther(lottery[5]),
      participantCount: Number(lottery[6]),
      winner: lottery[7],
      isActive: lottery[8],
      isEnded: lottery[9],
      sequenceNumber: Number(lottery[10])
    };
  }

  // Get lottery participants
  async getLotteryParticipants(lotteryId: number): Promise<string[]> {
    if (!this.contract) throw new Error('Contract not initialized');
    
    return await this.contract.getLotteryParticipants(lotteryId);
  }

  // Get user statistics
  async getUserStats(userAddress: string): Promise<UserStats> {
    if (!this.contract) throw new Error('Contract not initialized');
    
    const [winnings, participations] = await this.contract.getUserStats(userAddress);
    
    return {
      winnings: ethers.utils.formatEther(winnings),
      participations: Number(participations)
    };
  }

  // Check if user has participated in a lottery
  async hasParticipated(lotteryId: number, userAddress: string): Promise<boolean> {
    if (!this.contract) throw new Error('Contract not initialized');
    
    return await this.contract.hasParticipated(lotteryId, userAddress);
  }

  // Join a lottery
  async joinLottery(lotteryId: number, entryFee: string): Promise<string> {
    if (!this.contract || !this.signer) throw new Error('Contract or signer not initialized');
    
    const tx = await this.contract.joinLottery(lotteryId, {
      value: ethers.utils.parseEther(entryFee)
    });
    
    await tx.wait();
    return tx.hash;
  }

  // Claim prize
  async claimPrize(lotteryId: number): Promise<string> {
    if (!this.contract || !this.signer) throw new Error('Contract or signer not initialized');
    
    const tx = await this.contract.claimPrize(lotteryId);
    await tx.wait();
    return tx.hash;
  }

  // Get entropy fee
  async getEntropyFee(): Promise<string> {
    if (!this.contract) throw new Error('Contract not initialized');
    
    const fee = await this.contract.getEntropyFee();
    return ethers.utils.formatEther(fee);
  }

  // Get contract constants
  async getConstants() {
    if (!this.contract) throw new Error('Contract not initialized');
    
    const [
      minEntryFee,
      maxEntryFee,
      minParticipants,
      maxParticipants,
      minDuration,
      maxDuration
    ] = await Promise.all([
      this.contract.MIN_ENTRY_FEE(),
      this.contract.MAX_ENTRY_FEE(),
      this.contract.MIN_PARTICIPANTS(),
      this.contract.MAX_PARTICIPANTS(),
      this.contract.MIN_DURATION(),
      this.contract.MAX_DURATION()
    ]);

    return {
      minEntryFee: ethers.utils.formatEther(minEntryFee),
      maxEntryFee: ethers.utils.formatEther(maxEntryFee),
      minParticipants: Number(minParticipants),
      maxParticipants: Number(maxParticipants),
      minDuration: Number(minDuration),
      maxDuration: Number(maxDuration)
    };
  }

  // Listen to lottery events
  onLotteryCreated(callback: (lotteryId: number, entryFee: string, maxParticipants: number, duration: number) => void) {
    if (!this.contract) throw new Error('Contract not initialized');
    
    this.contract.on('LotteryCreated', callback);
  }

  onLotteryJoined(callback: (lotteryId: number, participant: string, participantCount: number) => void) {
    if (!this.contract) throw new Error('Contract not initialized');
    
    this.contract.on('LotteryJoined', callback);
  }

  onLotteryEnded(callback: (lotteryId: number, winner: string, prizeAmount: string, sequenceNumber: number) => void) {
    if (!this.contract) throw new Error('Contract not initialized');
    
    this.contract.on('LotteryEnded', callback);
  }

  onPrizeClaimed(callback: (lotteryId: number, winner: string, amount: string) => void) {
    if (!this.contract) throw new Error('Contract not initialized');
    
    this.contract.on('PrizeClaimed', callback);
  }

  onRandomNumberRequested(callback: (lotteryId: number, sequenceNumber: number) => void) {
    if (!this.contract) throw new Error('Contract not initialized');
    
    this.contract.on('RandomNumberRequested', callback);
  }

  // Remove event listeners
  removeAllListeners() {
    if (this.contract) {
      this.contract.removeAllListeners();
    }
  }

  // Utility functions
  formatTime(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleString();
  }

  getTimeRemaining(endTime: number): string {
    const now = Math.floor(Date.now() / 1000);
    const remaining = endTime - now;
    
    if (remaining <= 0) return 'Ended';
    
    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;
    
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  isLotteryActive(lottery: LotteryInfo): boolean {
    const now = Math.floor(Date.now() / 1000);
    return lottery.isActive && now < lottery.endTime && lottery.participantCount < lottery.maxParticipants;
  }

  canJoinLottery(lottery: LotteryInfo, userAddress: string): boolean {
    return this.isLotteryActive(lottery) && lottery.winner === ethers.constants.AddressZero;
  }

  canClaimPrize(lottery: LotteryInfo, userAddress: string): boolean {
    return lottery.isEnded && lottery.winner.toLowerCase() === userAddress.toLowerCase() && lottery.prizePool !== '0.0';
  }
}

export const lotteryService = new LotteryService();
